/**
 * ProShip Direct v2 — bulk rule upload: template, parse, validate.
 *
 * Pure functions, no React, so the validation is testable and the modal stays presentational.
 *
 * Nothing is created until every row has been checked and the merchant has seen what was
 * rejected — the same contract as the serviceability upload, which is the house pattern for
 * "upload a file that changes how orders ship".
 *
 * There is no bulk endpoint: `ruleengine/create_rule` and `ruleengine/setMerchantRule` are
 * single-rule calls, so a real version either loops them server-side or gains a bulk route.
 * Parsing here is deliberately strict, because a silently misread rule misroutes real orders.
 *
 * DEVIATION: this is a light CSV read (split on newline, then comma) rather than a full parser.
 * The template therefore uses `|` inside a cell for multi-values and never a comma. A quoted
 * comma in a cell would be misread, so any row whose column count is wrong is rejected outright
 * rather than guessed at.
 */
import { RULE_CONDITION_TYPES, PRIORITY_STRATEGIES } from './defaults';

/**
 * Every condition the builder can express has a column, so download → edit → upload is lossless.
 * A narrower template would silently drop conditions on the way out, which is worse than a wide
 * one: the merchant would re-upload a rule they believe is the same and get a different rule.
 */
export const RULE_CSV_COLUMNS = [
  'rule_name',
  'payment_mode',
  'zone',
  'weight_gte',
  'weight_lte',
  'order_value_gte',
  'order_value_lte',
  'quantity_gte',
  'quantity_lte',
  'channel',
  'sku',
  'pincode',
  'state',
  'city',
  'tag',
  'strategy',
  'courier_priority',
];

const ZONE_VALUES = RULE_CONDITION_TYPES.find((t) => t.key === 'zone').options.map((o) => o.value);
const CHANNEL_VALUES = RULE_CONDITION_TYPES.find((t) => t.key === 'channel').options.map((o) => o.value);
const STRATEGY_VALUES = PRIORITY_STRATEGIES.map((s) => s.key);

/** Build one row from an object keyed by column name, so examples cannot drift out of order. */
const toRow = (values) => RULE_CSV_COLUMNS.map((c) => values[c] ?? '').join(',');

/** A filled example, so the merchant can see the shape rather than infer it from headers. */
export const RULE_CSV_TEMPLATE = [
  RULE_CSV_COLUMNS.join(','),
  toRow({
    rule_name: 'COD heavy to metros',
    payment_mode: 'COD',
    zone: 'METROS',
    weight_gte: '1000',
    strategy: 'PERSONALIZED',
    courier_priority: 'ABC_DL_SURFACE_PREPAID|ABC_BD_AIR_COD',
  }),
  toRow({
    rule_name: 'High value prepaid',
    payment_mode: 'PREPAID',
    order_value_gte: '5000',
    channel: 'SHOPIFY',
    strategy: 'FASTEST',
  }),
  toRow({
    rule_name: 'Fragile single items',
    quantity_lte: '1',
    tag: 'fragile',
    strategy: 'FASTEST',
  }),
  toRow({
    rule_name: 'Small parcels cheapest',
    zone: 'LOCAL|REGIONAL',
    weight_lte: '500',
    strategy: 'CHEAPEST',
  }),
  toRow({ rule_name: 'Everything else', strategy: 'PACE' }),
].join('\n');

/**
 * Serialise existing rules back into the upload format.
 *
 * Commas inside a value would break the light parser on the way back in, so multi-values are
 * rejoined with `|` and a stray comma in free text is replaced with one. Anything the columns
 * cannot hold would be a silent loss, which is why every condition type has a column.
 */
export function rulesToCsv(rules) {
  const clean = (value) => String(value ?? '').replace(/,/g, '|');

  const rows = rules.map((rule) => {
    const values = { rule_name: clean(rule.name), strategy: rule.strategy };

    (rule.conditions || []).forEach((c) => {
      const suffix = c.operator === 'LTE' || c.operator === 'LT' ? 'lte' : 'gte';
      switch (c.type) {
        case 'paymentMode':
          values.payment_mode = c.value;
          break;
        case 'zone':
        case 'channel':
          values[c.type === 'zone' ? 'zone' : 'channel'] = (c.value || []).join('|');
          break;
        case 'weight':
          values[`weight_${suffix}`] = c.value;
          break;
        case 'orderValue':
          values[`order_value_${suffix}`] = c.value;
          break;
        case 'quantity':
          values[`quantity_${suffix}`] = c.value;
          break;
        default:
          // sku, pincode, state, city, tag — all free-text or pasted lists.
          values[c.type] = clean(c.value);
      }
    });

    values.courier_priority = (rule.order || []).map((key) => key.split(':')[1]).join('|');
    return toRow(values);
  });

  return [RULE_CSV_COLUMNS.join(','), ...rows].join('\n');
}

const splitMulti = (cell) =>
  String(cell || '')
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean);

const isNumber = (value) => value !== '' && !Number.isNaN(Number(value));

/**
 * Parse and validate a rule CSV.
 *
 * @param text          file contents
 * @param existingNames rule names already in use, so a duplicate is caught before it is created
 * @param courierCodes  codes of enabled couriers, so a priority list cannot name a courier that
 *                      allocation will never reach
 * @returns {{valid: Array, rejected: Array<{row: number, name: string, reason: string}>}}
 */
export function parseRuleCsv(text, existingNames = [], courierCodes = []) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { valid: [], rejected: [{ row: 0, name: '—', reason: 'The file is empty' }] };
  }

  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const headerMatches =
    header.length === RULE_CSV_COLUMNS.length &&
    RULE_CSV_COLUMNS.every((c, i) => header[i] === c);

  if (!headerMatches) {
    return {
      valid: [],
      rejected: [
        {
          row: 1,
          name: '—',
          reason: 'Header row does not match the template — download it again and keep the columns in order',
        },
      ],
    };
  }

  const valid = [];
  const rejected = [];
  const seenNames = new Set(existingNames.map((n) => n.toLowerCase()));

  lines.slice(1).forEach((line, i) => {
    const row = i + 2; // 1-based, and the header is row 1
    const cells = line.split(',').map((c) => c.trim());

    const reject = (reason, name = cells[0] || '—') => rejected.push({ row, name, reason });

    if (cells.length !== RULE_CSV_COLUMNS.length) {
      reject(`Expected ${RULE_CSV_COLUMNS.length} columns, found ${cells.length}`);
      return;
    }

    // Read by column name rather than by position — 17 positional variables is a bug waiting to
    // happen the next time a column is added.
    const cell = (column) => cells[RULE_CSV_COLUMNS.indexOf(column)] || '';
    const name = cell('rule_name');
    const paymentMode = cell('payment_mode');
    const zone = cell('zone');
    const weightGte = cell('weight_gte');
    const weightLte = cell('weight_lte');
    const valueGte = cell('order_value_gte');
    const valueLte = cell('order_value_lte');
    const channel = cell('channel');
    const sku = cell('sku');
    const strategy = cell('strategy');
    const priority = cell('courier_priority');

    if (!name) {
      reject('Rule name is required');
      return;
    }
    if (seenNames.has(name.toLowerCase())) {
      reject('A rule with this name already exists');
      return;
    }

    const conditions = [];

    if (paymentMode) {
      if (!['PREPAID', 'COD'].includes(paymentMode.toUpperCase())) {
        reject(`Payment mode must be PREPAID or COD, found "${paymentMode}"`);
        return;
      }
      conditions.push({ type: 'paymentMode', value: paymentMode.toUpperCase() });
    }

    if (zone) {
      const zones = splitMulti(zone).map((z) => z.toUpperCase());
      const unknown = zones.filter((z) => !ZONE_VALUES.includes(z));
      if (unknown.length) {
        reject(`Unknown zone ${unknown.join(', ')} — use ${ZONE_VALUES.join(', ')}`);
        return;
      }
      conditions.push({ type: 'zone', value: zones });
    }

    if (channel) {
      const channels = splitMulti(channel).map((c) => c.toUpperCase());
      const unknown = channels.filter((c) => !CHANNEL_VALUES.includes(c));
      if (unknown.length) {
        reject(`Unknown channel ${unknown.join(', ')}`);
        return;
      }
      conditions.push({ type: 'channel', value: channels });
    }

    const bounds = [
      { cell: weightGte, type: 'weight', operator: 'GTE', label: 'weight_gte' },
      { cell: weightLte, type: 'weight', operator: 'LTE', label: 'weight_lte' },
      { cell: valueGte, type: 'orderValue', operator: 'GTE', label: 'order_value_gte' },
      { cell: valueLte, type: 'orderValue', operator: 'LTE', label: 'order_value_lte' },
      { cell: cell('quantity_gte'), type: 'quantity', operator: 'GTE', label: 'quantity_gte' },
      { cell: cell('quantity_lte'), type: 'quantity', operator: 'LTE', label: 'quantity_lte' },
    ];

    let numericError = null;
    bounds.forEach((b) => {
      if (!b.cell) return;
      if (!isNumber(b.cell)) {
        numericError = `${b.label} must be a number, found "${b.cell}"`;
        return;
      }
      conditions.push({ type: b.type, operator: b.operator, value: String(Number(b.cell)) });
    });
    if (numericError) {
      reject(numericError);
      return;
    }

    // A rule matching weight ≥ 5000 and ≤ 1000 can never fire — catch it here, not in production.
    const gte = weightGte && Number(weightGte);
    const lte = weightLte && Number(weightLte);
    if (gte && lte && gte > lte) {
      reject('weight_gte is greater than weight_lte, so the rule can never match');
      return;
    }
    const vGte = valueGte && Number(valueGte);
    const vLte = valueLte && Number(valueLte);
    if (vGte && vLte && vGte > vLte) {
      reject('order_value_gte is greater than order_value_lte, so the rule can never match');
      return;
    }

    // Free-text and pasted-list conditions: stored comma-separated, carried as pipe-separated.
    [
      { column: 'sku', type: 'sku', raw: sku },
      { column: 'pincode', type: 'pincode', raw: cell('pincode') },
      { column: 'state', type: 'state', raw: cell('state') },
      { column: 'city', type: 'city', raw: cell('city') },
      { column: 'tag', type: 'tag', raw: cell('tag') },
    ].forEach(({ type, raw }) => {
      if (raw) conditions.push({ type, value: splitMulti(raw).join(', ') });
    });

    const strategyKey = (strategy || 'PACE').toUpperCase();
    if (!STRATEGY_VALUES.includes(strategyKey)) {
      reject(`Strategy must be one of ${STRATEGY_VALUES.join(', ')}`);
      return;
    }

    const order = splitMulti(priority).map((code) => `${courierCodes.includes(code) ? 'direct' : 'proship'}:${code}`);

    if (strategyKey === 'PERSONALIZED' && order.length === 0) {
      reject('PERSONALIZED needs at least one courier code in courier_priority');
      return;
    }
    if (strategyKey !== 'PERSONALIZED' && order.length > 0) {
      reject(`courier_priority only applies to PERSONALIZED, but strategy is ${strategyKey}`);
      return;
    }

    const unknownCouriers = splitMulti(priority).filter((code) => !courierCodes.includes(code));
    if (unknownCouriers.length) {
      reject(`Not an enabled courier code: ${unknownCouriers.join(', ')}`);
      return;
    }

    seenNames.add(name.toLowerCase());
    valid.push({ name, conditions, strategy: strategyKey, order });
  });

  return { valid, rejected };
}
